import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, DollarSign, ChevronRight, Star,
  Volume2, Copy, Check, Sun, Moon, Users, Utensils,
} from 'lucide-react';

// ─── Tonight's Plan ─────────────────────────────────────────────────
// You're about to clock in. You're tired. You skipped lunch.
// This screen tells you exactly what to do tonight to make the
// most money possible. No fluff. Just the plan.

interface PushItem {
  name: string;
  price: string;
  yourCut: string;
  script: string;
  why: string;
  successRate: string;
}

const TONIGHT: {
  shift: string;
  expectedCovers: number;
  yourTables: number;
  specials: PushItem[];
  drinkPush: PushItem;
  dessertPush: PushItem;
  targetEarnings: number;
  weatherNote: string;
} = {
  shift: 'Friday Dinner · 5:00 PM – 10:30 PM',
  expectedCovers: 180,
  yourTables: 6,
  specials: [
    {
      name: 'Wagyu Platter',
      price: '$34',
      yourCut: '~$6.80 tip per table',
      script: '"The Wagyu platter is incredible tonight — the kitchen just got a fresh cut in. If you like steak at all, this is the move."',
      why: 'Highest margin item. Tables that order it tip 20% more on average.',
      successRate: '68% of tables say yes when pitched',
    },
    {
      name: 'Döner Kebab Upgrade',
      price: '$18 → $26',
      yourCut: '~$1.60 extra per person',
      script: '"Want me to do the premium döner? It\'s the same kebab but with the house-made garlic sauce and extra meat. Totally worth the upgrade."',
      why: 'Easy upsell — they already want the döner. You\'re just upgrading it.',
      successRate: '54% upgrade rate',
    },
  ],
  drinkPush: {
    name: 'Mango Lassi',
    price: '$8',
    yourCut: '~$1.60 per drink',
    script: '"Can I start you off with our Mango Lassi? It\'s house-made and it\'s honestly the best thing on the menu for a hot day like today."',
    why: 'High margin drink. Easy sell because it sounds refreshing.',
    successRate: '71% say yes on warm days',
  },
  dessertPush: {
    name: 'Dubai Chocolate',
    price: '$14',
    yourCut: '~$2.80 per table',
    script: '"Before I bring the check — have you had our Dubai Chocolate? It\'s this insane pistachio-filled chocolate thing. I\'ll bring one for the table to share?"',
    why: 'Suggesting "for the table to share" removes the guilt of ordering dessert alone.',
    successRate: '43% take rate (61% when positioned as sharing)',
  },
  targetEarnings: 185,
  weatherNote: 'Warm evening (74°F) — lean into cold drinks',
};

const TonightsPlan: React.FC = () => {
  const navigate = useNavigate();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text.replace(/"/g, ''));
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const ScriptCard = ({ item, idx }: { item: PushItem; idx: number }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{item.name}</h3>
            <div className="flex items-center gap-3 text-sm mt-0.5">
              <span className="text-gray-500">{item.price}</span>
              <span className="text-emerald-600 font-semibold">{item.yourCut}</span>
            </div>
          </div>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{item.successRate}</span>
        </div>

        {/* The script — this is the important part */}
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 relative">
          <div className="flex items-start gap-2">
            <Volume2 className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed italic">{item.script}</p>
          </div>
          <button
            onClick={() => handleCopy(item.script, idx)}
            className="absolute top-2 right-2 p-1 text-amber-400 hover:text-amber-600 transition-colors"
            title="Copy script"
          >
            {copiedIdx === idx ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-2">{item.why}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Sun className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Tonight's Plan</h1>
              <p className="text-gray-400 text-sm">{TONIGHT.shift}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Tonight at a glance */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Your shift at a glance</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Users className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-gray-900">{TONIGHT.expectedCovers}</div>
              <div className="text-[10px] text-gray-500">expected covers</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Utensils className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-gray-900">{TONIGHT.yourTables}</div>
              <div className="text-[10px] text-gray-500">tables in your section</div>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <DollarSign className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
              <div className="text-lg font-bold text-emerald-700">${TONIGHT.targetEarnings}</div>
              <div className="text-[10px] text-emerald-600">realistic target</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Sun className="w-4 h-4 text-amber-400 mx-auto mb-1" />
              <div className="text-xs font-medium text-gray-700 mt-1">{TONIGHT.weatherNote}</div>
            </div>
          </div>
        </div>

        {/* The real talk */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-sm text-emerald-800">
            <span className="font-semibold">Here's the math:</span> If you push the Wagyu to half your tables and
            land the drink on most, that's an extra <span className="font-bold">$35-45 in tips</span> tonight
            compared to doing nothing. That's grocery money. That's gas money. Let's get it.
          </p>
        </div>

        {/* Push items */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">What to push tonight</h2>
          <p className="text-xs text-gray-500 mb-3">
            These are your highest-impact items right now. Each card has the exact words to use —
            you don't have to figure anything out, just say what's in the yellow box.
          </p>
          <div className="space-y-3">
            {TONIGHT.specials.map((item, i) => (
              <ScriptCard key={i} item={item} idx={i} />
            ))}
          </div>
        </div>

        {/* Drinks */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Drink to push</h2>
          <ScriptCard item={TONIGHT.drinkPush} idx={10} />
        </div>

        {/* Dessert */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">End-of-meal closer</h2>
          <ScriptCard item={TONIGHT.dessertPush} idx={11} />
        </div>

        {/* Quick reference */}
        <div className="bg-gray-900 rounded-xl p-5 text-white">
          <h2 className="text-sm font-semibold mb-3">Quick Reference: Your tip math</h2>
          <div className="space-y-2 text-sm">
            {[
              { action: 'Land 1 Wagyu per hour', result: '+$34 in checks', tips: '~$6.80 more in tips' },
              { action: 'Sell 2 Mango Lassis per hour', result: '+$16 in checks', tips: '~$3.20 more in tips' },
              { action: 'Close 1 Dubai Chocolate per hour', result: '+$14 in checks', tips: '~$2.80 more in tips' },
              { action: 'All of the above for 5 hours', result: '+$320 in checks', tips: '~$64 more in tips' },
            ].map((row, i) => (
              <div key={i} className={`flex items-center justify-between py-2 ${i < 3 ? 'border-b border-gray-800' : 'border-t-2 border-gray-700 pt-3 mt-1'}`}>
                <span className="text-gray-300">{row.action}</span>
                <div className="text-right">
                  <span className="text-gray-500 text-xs mr-3">{row.result}</span>
                  <span className={`font-semibold ${i === 3 ? 'text-emerald-400 text-base' : 'text-emerald-400'}`}>{row.tips}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Encouragement */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">
            You've got this. One table at a time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TonightsPlan;
